import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@acast-tech/auth-client';
import { config } from '@/config';
import { fetchGraphQL } from '@/services/graphqlClient';

const VIEWER_LOCALE_QUERY = `
  query ViewerLocale {
    viewer {
      locale
    }
  }
`;

interface ViewerLocaleResponse {
  viewer: {
    locale: string | null;
  };
}

const fetchViewerLocale = async (token: string): Promise<ViewerLocaleResponse> =>
  fetchGraphQL<ViewerLocaleResponse>({
    url: config.graphqlApiUrl,
    token,
    query: VIEWER_LOCALE_QUERY,
    headers: { 'Client-Name': 'revenue/echo-nav' },
  });

export interface ViewerLocaleResult {
  locale: string | null;
  isAccountLocaleReady: boolean;
}

/**
 * Account locale from GraphQL `viewer.locale`. Used for UI catalogs, not number formatting.
 * `isAccountLocaleReady` is false while the query is in flight so callers do not treat
 * a missing value as "use the browser language".
 */
export const useViewerLocale = (): ViewerLocaleResult => {
  const { userId, getToken } = useAuth();
  const isQueryEnabled = Boolean(userId);

  const { data, isFetched } = useQuery({
    queryKey: ['viewer-locale'],
    queryFn: async () => {
      const token = await getToken();
      return fetchViewerLocale(token);
    },
    enabled: isQueryEnabled,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  return {
    locale: data?.viewer.locale ?? null,
    isAccountLocaleReady: !isQueryEnabled || isFetched,
  };
};
